import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, FormControl, Grid2, InputLabel,
    MenuItem, Select, Snackbar, Stack, TextField, Typography
} from '@mui/material';
import {
    Dining, LocalShipping, QrCodeScanner, Refresh, Restaurant,
    Search, TakeoutDining
} from '@mui/icons-material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import Navbar from '../setup/Navbar';
import '../../services/GlobalVariables';

const STATUSES = [
    { value: 'preparing', label: 'Preparing', color: '#ed6c02', icon: <Restaurant /> }
];
const normalizeServiceStatus = (status) => ['completed', 'cancelled'].includes(String(status || '').toLowerCase()) ? String(status).toLowerCase() : 'preparing';

const itemLines = (order) => String(order?.itemsSummary || '').split('||').filter(Boolean);
const isFoodpandaOrder = (order) => String(order?.orderType || '').toLowerCase() === 'delivery' && String(order?.aggregatorPlatform || '').toLowerCase() === 'foodpanda';
const displayOrderNumber = (order) => order?.announcementKotNo || order?.lastKotNo || order?.firstKotNo || order?.orderID || '';
const readyCountdown = (order, now) => {
    const elapsed = Number(order?.secondsSinceCompleted || 0) + Math.floor((now - Number(order?.readyFetchedAt || now)) / 1000);
    const remaining = Math.max(0, 120 - elapsed);
    return String(Math.floor(remaining / 60)).padStart(2, String(0)) + ':' + String(remaining % 60).padStart(2, String(0));
};

export default function DineTakeawayDashboard({ mode = 'takeaway' }) {
    const { user } = useSelector((state) => state.auth || {});
    const [orders, setOrders] = useState([]);
    const [readyForPickup, setReadyForPickup] = useState([]);
    const [readyClock, setReadyClock] = useState(Date.now());
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState('all');
    const [scanCode, setScanCode] = useState('');
    const [highlightedOrderID, setHighlightedOrderID] = useState(null);
    const [notice, setNotice] = useState({ open: false, message: '', severity: 'success' });
    const scanInputRef = useRef(null);
    const cardRefs = useRef({});
    const readyOrderIdsRef = useRef(new Set());
    const readyOrdersLoadedRef = useRef(false);

    const notify = (message, severity = 'success') => setNotice({ open: true, message, severity });

    const announceReadyOrder = useCallback((order) => {
        if (!('speechSynthesis' in window)) return;
        const number = String(order.announcementKotNo || order.lastKotNo || order.firstKotNo || order.orderID || '');
        const digitWords = { 0: 'zero', 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight', 9: 'nine' };
        const spokenNumber = number.split('').map((character) => digitWords[character] || character).join(' ');
        const voices = window.speechSynthesis.getVoices();
        const femaleVoice = voices.find((voice) => /zira|hazel|susan|samantha|victoria|ava|jenny|aria|sonia|natasha|libby|female/i.test(voice.name));
        const englishAnnouncement = new SpeechSynthesisUtterance('Order number ' + spokenNumber + ' has been ready to pickup.');
        englishAnnouncement.lang = 'en-US';
        englishAnnouncement.rate = 0.7;
        if (femaleVoice) englishAnnouncement.voice = femaleVoice;

        // Keep the announcement English-only until Urdu voice support is integrated.
        window.speechSynthesis.cancel();
        window.speechSynthesis.resume();
        for (let repeat = 0; repeat < 3; repeat += 1) {
            const announcement = new SpeechSynthesisUtterance(englishAnnouncement.text);
            announcement.lang = englishAnnouncement.lang;
            announcement.rate = englishAnnouncement.rate;
            if (femaleVoice) announcement.voice = femaleVoice;
            window.speechSynthesis.speak(announcement);
        }
    }, []);

    const fetchDashboard = useCallback(async () => {
        try {
            const response = await axios.get(`${global.SET_API_URL}dineTakeaway/dashboard?scope=${mode === 'dinein' ? 'dinein' : 'takeaway'}`);
            setOrders(response.data?.data?.orders || []);
            const readyOrders = response.data?.data?.readyForPickup || [];
            const nextReadyOrderIds = new Set(readyOrders.map((order) => String(order.orderID)));
            if (readyOrdersLoadedRef.current) {
                readyOrders
                    .filter((order) => !readyOrderIdsRef.current.has(String(order.orderID)))
                    .forEach((order) => announceReadyOrder(order));
            }
            readyOrderIdsRef.current = nextReadyOrderIds;
            readyOrdersLoadedRef.current = true;
            setReadyForPickup(readyOrders.map((order) => ({ ...order, readyFetchedAt: Date.now() })));
        } catch (error) {
            notify(error.response?.data?.message || 'Dashboard load failed', 'error');
        } finally {
            setLoading(false);
        }
    }, [announceReadyOrder, mode]);

    useEffect(() => {
        const timer = window.setInterval(() => setReadyClock(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        fetchDashboard();
        const timer = setInterval(fetchDashboard, 10000);
        return () => clearInterval(timer);
    }, [fetchDashboard]);

    useEffect(() => {
        const events = new EventSource(`${global.SET_API_URL}kitchenOrderScreen/events`);
        const refresh = () => fetchDashboard();
        events.addEventListener('kitchen-order-updated', refresh);
        return () => {
            events.removeEventListener('kitchen-order-updated', refresh);
            events.close();
        };
    }, [fetchDashboard]);

    const filteredOrders = useMemo(() => typeFilter === 'all'
        ? orders
        : orders.filter((order) => String(order.orderType).toLowerCase() === typeFilter), [orders, typeFilter]);

    const preparationSections = useMemo(() => {
        const preparing = filteredOrders.filter((order) => normalizeServiceStatus(order.serviceStatus) === 'preparing');
        if (mode === 'dinein') return [{ value: 'dinein', label: 'Dine-In Preparing', color: '#1565c0', icon: <Dining />, orders: preparing }];
        return [
            { value: 'takeaway', label: 'Takeaway Preparing', color: '#6a1b9a', icon: <TakeoutDining />, orders: preparing.filter((order) => String(order.orderType || '').toLowerCase() === 'takeaway') },
            { value: 'delivery', label: 'Delivery Preparing', color: '#ef6c00', icon: <LocalShipping />, orders: preparing.filter((order) => String(order.orderType || '').toLowerCase() === 'delivery' && !isFoodpandaOrder(order)) },
            { value: 'foodpanda', label: 'FoodPanda Preparing', color: '#d70f64', icon: <LocalShipping />, orders: preparing.filter(isFoodpandaOrder) }
        ];
    }, [filteredOrders, mode]);

    const updateStatus = async (order, status) => {
        try {
            await axios.post(`${global.SET_API_URL}dineTakeaway/status`, {
                orderID: order.orderID,
                status,
                changedBy: user?.userID || 1
            });
            notify(`Order #${order.orderID} moved to ${status.toUpperCase()}`);
            await fetchDashboard();
        } catch (error) {
            notify(error.response?.data?.message || 'Status update failed', 'error');
        }
    };

    const findScannedOrder = async () => {
        const code = scanCode.trim();
        if (!code) return;
        try {
            const response = await axios.post(`${global.SET_API_URL}dineTakeaway/scan`, { code });
            const found = response.data?.data;
            if (!found) throw new Error('Order not found');
            setTypeFilter('all');
            setHighlightedOrderID(found.orderID);
            if (!orders.some((order) => Number(order.orderID) === Number(found.orderID))) {
                setOrders((current) => [found, ...current]);
            }
            setTimeout(() => {
                cardRefs.current[found.orderID]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
            setTimeout(() => setHighlightedOrderID(null), 5000);
            notify(`Order #${found.orderID} found`);
            setScanCode('');
        } catch (error) {
            notify(error.response?.data?.message || error.message || 'Scanned order not found', 'error');
        }
    };

    const OrderCard = ({ order }) => {
        const type = String(order.orderType || '').toLowerCase();
        const isDispatch = type === 'dispatch';
        const lines = itemLines(order);
        return (
            <Card
                ref={(node) => { cardRefs.current[order.orderID] = node; }}
                sx={{
                    mb: 1.5,
                    bgcolor: 'white',
                    color: 'text.primary',
                    border: '2px solid',
                    borderColor: highlightedOrderID === order.orderID ? 'success.main' : type === 'dinein' ? 'primary.main' : isDispatch ? 'success.main' : 'warning.main',
                    boxShadow: highlightedOrderID === order.orderID ? 8 : 1,
                    transition: 'all .2s'
                }}
            >
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography variant="h6">Order #{displayOrderNumber(order)}</Typography>
                        <Button size="small" variant="contained" color="success" onClick={() => updateStatus(order, 'completed')} sx={{ minWidth: 112, fontSize: '0.72rem', fontWeight: 800, whiteSpace: 'nowrap' }}>
                            MARK COMPLETE
                        </Button>
                        <Chip size="small" label={`${order.minutesSinceOrder || 0} min`} color={Number(order.minutesSinceOrder) > 30 ? 'error' : 'default'} />
                    </Stack>
                    {lines.length > 0 && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                            {lines.slice(0, 6).map((line, index) => <Typography key={index} variant="caption" display="block">{line}</Typography>)}
                            {lines.length > 6 && <Typography variant="caption">+{lines.length - 6} more</Typography>}
                        </Box>
                    )}

                </CardContent>
            </Card>
        );
    };

    return (
        <>
            <Navbar />
            <Box sx={{ minHeight: '100vh', bgcolor: '#101820', color: 'white', p: { xs: 2, md: 3 }, '& .MuiTypography-colorTextSecondary': { color: '#b0bec5' } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
                    <Box>
                        <Typography variant="h4" fontWeight={700}>{mode === 'dinein' ? 'Dine-In Order Tracking' : 'Takeaway Counter Order Tracking'}</Typography>
                        <Typography color="text.secondary">Live service status, manual control, barcode/QR lookup</Typography>
                    </Box>
                    <Button variant="outlined" startIcon={<Refresh />} onClick={fetchDashboard} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</Button>
                </Stack>

                <Card sx={{ mb: 2, bgcolor: '#fff3cd', border: '3px solid #ffb300', color: 'text.primary' }}>
                    <CardContent>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                            <FormControl size="small" sx={{ minWidth: 190 }}>
                                <InputLabel>Order Type</InputLabel>
                                <Select value={typeFilter} label="Order Type" onChange={(event) => setTypeFilter(event.target.value)}>
                                    <MenuItem value="all">All Orders</MenuItem>
                                    <MenuItem value="dinein">Dine-In</MenuItem>
                                    <MenuItem value="takeaway">Takeaway</MenuItem>
                                    <MenuItem value="dispatch">Dispatch</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                inputRef={scanInputRef}
                                fullWidth
                                size="small"
                                label="Scan Barcode / QR or enter Order ID, KOT #, Ticket #"
                                value={scanCode}
                                onChange={(event) => setScanCode(event.target.value)}
                                onKeyDown={(event) => { if (event.key === 'Enter') findScannedOrder(); }}
                                slotProps={{ input: { startAdornment: <QrCodeScanner sx={{ mr: 1, color: 'text.secondary' }} /> } }}
                            />
                            <Button variant="contained" startIcon={<Search />} onClick={findScannedOrder} sx={{ minWidth: 130 }}>Find</Button>
                            <Button variant="outlined" startIcon={<QrCodeScanner />} onClick={() => scanInputRef.current?.focus()} sx={{ minWidth: 150 }}>Start Scan</Button>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">USB/Bluetooth barcode and QR scanners: scan code and Enter will search automatically.</Typography>
                    </CardContent>
                </Card>

                <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, md: 8 }}>
                        <Grid2 container spacing={2}>
                    {preparationSections.map((status) => (
                        <Grid2 key={status.value} size={{ xs: 12, md: mode === 'dinein' ? 12 : 4 }}>
                            <Card sx={{ bgcolor: status.color, color: 'white', mb: 1.5 }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Stack direction="row" spacing={1} alignItems="center">{status.icon}<Typography variant="h6">{status.label}</Typography></Stack>
                                        <Chip label={status.orders.length} sx={{ bgcolor: 'white', fontWeight: 700 }} />
                                    </Stack>
                                </CardContent>
                            </Card>
                            <Box sx={{ maxHeight: 'calc(100vh - 315px)', overflowY: 'auto', pr: 0.5 }}>
                                {status.orders.length
                                    ? status.orders.map((order) => <OrderCard key={order.orderID} order={order} />)
                                    : <Card sx={{ bgcolor: '#18232d', color: 'white', border: '1px solid #33424f' }}><CardContent><Typography align='center' color='grey.500'>No orders</Typography></CardContent></Card>}
                            </Box>
                        </Grid2>
                    ))}
                        </Grid2>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <Card sx={{ bgcolor: '#8b0000', color: 'white', mb: 1.5 }}>
                            <CardContent sx={{ py: 1.5 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Stack direction="row" spacing={1} alignItems="center"><TakeoutDining /><Typography variant="h6">Order Ready to Pickup</Typography></Stack>
                                    <Chip label={readyForPickup.length} sx={{ bgcolor: 'white', fontWeight: 700 }} />
                                </Stack>
                            </CardContent>
                        </Card>
                        <Box sx={{ maxHeight: 'calc(100vh - 315px)', overflowY: 'auto', pr: 0.5 }}>
                            {readyForPickup.length ? readyForPickup.map((order) => (
                                <Card key={order.orderID} sx={{ mb: 1, bgcolor: 'white', color: 'text.primary', border: '2px solid', borderColor: 'success.main' }}>
                                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                            <Typography variant="h6" fontWeight={800}>Order #{displayOrderNumber(order)}</Typography>
                                            <Chip color="success" label="READY" sx={{ height: 34, fontSize: '0.9rem', fontWeight: 900, letterSpacing: 0.8, px: 0.75 }} />
                                        </Stack>
                                    </CardContent>
                                </Card>
                            )) : <Card sx={{ bgcolor: '#18232d', color: 'white', border: '1px solid #33424f' }}><CardContent><Typography align='center' color='grey.500'>No orders ready for pickup</Typography></CardContent></Card>}
                        </Box>
                    </Grid2>
                </Grid2>
            </Box>
            <Snackbar open={notice.open} autoHideDuration={3500} onClose={() => setNotice((old) => ({ ...old, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert severity={notice.severity} variant="filled" onClose={() => setNotice((old) => ({ ...old, open: false }))}>{notice.message}</Alert>
            </Snackbar>
        </>
    );
}
